import { ThemeEvents } from '@theme/events';

/**
 * Capacity notice (blocks/capacity-notice.liquid)
 *
 * Shows the notice while a trigger add-on (e.g. Fluoride Filters) is selected
 * in the buy box, by listening for the bubbling `accessory:change` events the
 * buy-box flow and accessory widgets already dispatch. Matches by the trigger
 * product's variant ids, or by a fallback keyword against the add-on title.
 *
 * Each dispatching widget sends its own full selection list, so match state is
 * tracked per event source: the notice hides only when no source matches.
 */
class CapacityNotice extends HTMLElement {
  /** @type {Set<string>} */
  #variantIds = new Set();
  #keyword = '';
  /** @type {Map<EventTarget|null, boolean>} */
  #sources = new Map();

  connectedCallback() {
    this.#variantIds = new Set((this.dataset.variantIds || '').split(',').filter(Boolean));
    this.#keyword = (this.dataset.keyword || '').trim().toLowerCase();
    document.addEventListener(ThemeEvents.accessoryChange, this.#onAccessoryChange);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.accessoryChange, this.#onAccessoryChange);
  }

  /**
   * @param {{ variantId?: number | string, title?: string }} accessory
   * @returns {boolean}
   */
  #matches(accessory) {
    if (this.#variantIds.size > 0) return this.#variantIds.has(String(accessory.variantId));
    if (this.#keyword) return String(accessory.title || '').toLowerCase().includes(this.#keyword);
    return false;
  }

  /** @param {Event & { detail?: { accessories?: object[] } }} event */
  #onAccessoryChange = (event) => {
    const accessories = event.detail?.accessories || [];
    this.#sources.set(event.target, accessories.some((accessory) => this.#matches(accessory)));
    this.hidden = ![...this.#sources.values()].some(Boolean);
  };
}

if (!customElements.get('capacity-notice')) {
  customElements.define('capacity-notice', CapacityNotice);
}
